import { Flex, Text, TextField, Card } from '@scale/scaleui-radix';
import type { CalendarDataGeneratorContext } from '@scale/genai-compass-shared/cards/calendar_data_generator';

interface SamplingSettingsSectionProps {
  context: CalendarDataGeneratorContext;
  onChange: (updates: Partial<CalendarDataGeneratorContext>) => void;
}

// Helper function to format date as YYYY-MM-DD
const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

// Helper function to add months to a date
const addMonths = (date: Date, months: number): Date => {
  const result = new Date(date);
  result.setMonth(result.getMonth() + months);
  return result;
};

// Calculate default dates
const today = new Date();
const defaultEarliestDate = formatDate(addMonths(today, 2)); // 2 months after today
const defaultLatestDate = formatDate(addMonths(today, 4)); // 4 months after today

export default function SamplingSettingsSection({
  context,
  onChange,
}: SamplingSettingsSectionProps) {
  return (
    <Card style={{ padding: '16px' }}>
      <Flex direction="column" gap="4">
        <Text size="3" weight="bold" style={{ color: '#60a5fa' }}>
          DB Sampling Settings (per Task)
        </Text>

        <Flex direction="row" gap="4">
          <Flex direction="column" gap="2" style={{ flex: 1 }}>
            <Text size="2" weight="medium">
              Min Calendars
            </Text>
            <TextField.Root
              type="number"
              value={context.minCalendars || 1}
              onChange={e => onChange({ minCalendars: parseInt(e.target.value, 10) || 1 })}
              min={1}
            />
          </Flex>

          <Flex direction="column" gap="2" style={{ flex: 1 }}>
            <Text size="2" weight="medium">
              Max Calendars
            </Text>
            <TextField.Root
              type="number"
              value={context.maxCalendars || 3}
              onChange={e => onChange({ maxCalendars: parseInt(e.target.value, 10) || 3 })}
              min={1}
            />
          </Flex>
        </Flex>

        <Flex direction="row" gap="4">
          <Flex direction="column" gap="2" style={{ flex: 1 }}>
            <Text size="2" weight="medium">
              Min Events per Calendar
            </Text>
            <TextField.Root
              type="number"
              value={context.minEvents || 1}
              onChange={e => onChange({ minEvents: parseInt(e.target.value, 10) || 1 })}
              min={1}
            />
          </Flex>

          <Flex direction="column" gap="2" style={{ flex: 1 }}>
            <Text size="2" weight="medium">
              Max Events per Calendar
            </Text>
            <TextField.Root
              type="number"
              value={context.maxEvents || 10}
              onChange={e => onChange({ maxEvents: parseInt(e.target.value, 10) || 10 })}
              min={1}
            />
          </Flex>
        </Flex>

        <Flex direction="row" gap="4">
          <Flex direction="column" gap="2" style={{ flex: 1 }}>
            <Text size="2" weight="medium">
              Earliest Date (per Event)
            </Text>
            <TextField.Root
              type="date"
              value={context.earliestDate || defaultEarliestDate}
              onChange={e => onChange({ earliestDate: e.target.value })}
            />
          </Flex>

          <Flex direction="column" gap="2" style={{ flex: 1 }}>
            <Text size="2" weight="medium">
              Latest Date (per Event)
            </Text>
            <TextField.Root
              type="date"
              value={context.latestDate || defaultLatestDate}
              onChange={e => onChange({ latestDate: e.target.value })}
            />
          </Flex>
        </Flex>

        <Flex direction="column" gap="2" style={{ flex: 1 }}>
          <Text size="2" weight="medium">
            Max Users Per Event
          </Text>
          <TextField.Root
            type="number"
            value={context.maxUsers || 5}
            onChange={e => onChange({ maxUsers: parseInt(e.target.value, 10) || 5 })}
            min={1}
          />
        </Flex>
      </Flex>
    </Card>
  );
}
